import { useEffect, useState } from "react";
import { Loader2, Save, KeyRound, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", password: "" });
  const [userSaving, setUserSaving] = useState(false);

  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => toast.error("Failed to load settings"));
    loadUsers();
  }, []);

  const addUser = async (e) => {
    e.preventDefault();
    setUserSaving(true);
    try {
      await api.post("/admin/users", newUser);
      toast.success(`${newUser.email} added`);
      setNewUser({ email: "", password: "" });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "User add failed");
    } finally {
      setUserSaving(false);
    }
  };

  const deleteUser = async (email) => {
    try {
      await api.delete(`/admin/users/${email}`);
      toast.success(`${email} removed`);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed successfully");
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password change failed");
    } finally {
      setPwSaving(false);
    }
  };

  if (!settings) {
    return <div className="flex items-center gap-3 text-zinc-400" data-testid="settings-loading"><Loader2 className="w-5 h-5 animate-spin" /> Loading...</div>;
  }

  const set = (k) => (e) => setSettings({ ...settings, [k]: e.target.value });

  const Field = ({ id, label, value, onChange, placeholder, type = "text", testid }) => (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-zinc-300">{label}</Label>
      <Input
        id={id} type={type} value={value ?? ""} onChange={onChange} placeholder={placeholder}
        data-testid={testid}
        className="bg-black/40 border-white/15 h-11 rounded-xl focus-visible:ring-[#A78BFA]"
      />
    </div>
  );

  return (
    <div data-testid="admin-settings-page" className="max-w-2xl space-y-8">
      <div>
        <h1 className="font-['Outfit'] font-extrabold tracking-tight text-3xl mb-2">Settings</h1>
        <p className="text-zinc-500 text-sm">Control your product, payment gateway, pixel and password — all from here.</p>
      </div>

      <form onSubmit={save} className="space-y-8" data-testid="settings-form">
        <section className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 space-y-5">
          <h2 className="font-['Outfit'] font-bold text-lg">Product</h2>
          <Field id="product-name" label="Product Name" value={settings.product_name} onChange={set("product_name")} testid="setting-product-name" />
          <div className="grid grid-cols-2 gap-4">
            <Field id="price" label="Selling Price (₹)" type="number" value={settings.price} onChange={(e) => setSettings({ ...settings, price: Number(e.target.value) })} testid="setting-price" />
            <Field id="compare-price" label="Compare Price (₹)" type="number" value={settings.compare_price} onChange={(e) => setSettings({ ...settings, compare_price: Number(e.target.value) })} testid="setting-compare-price" />
          </div>
          <Field id="drive-link" label="Google Drive Product Link" value={settings.drive_link} onChange={set("drive_link")} placeholder="https://drive.google.com/..." testid="setting-drive-link" />
          <Field id="support-email" label="Support Email (shown on footer & legal pages)" value={settings.support_email} onChange={set("support_email")} placeholder="support@floxora.in" testid="setting-support-email" />
        </section>

        <section className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 space-y-5">
          <h2 className="font-['Outfit'] font-bold text-lg">Landing Content</h2>
          <p className="text-zinc-500 text-xs">Edit the hero headline, subtitle and scrolling marquee text of the landing page.</p>
          <Field id="hero-line1" label="Hero Line 1" value={settings.hero_line1} onChange={set("hero_line1")} testid="setting-hero-line1" />
          <Field id="hero-line2" label="Hero Line 2 (gold highlight)" value={settings.hero_line2} onChange={set("hero_line2")} testid="setting-hero-line2" />
          <Field id="hero-line3" label="Hero Line 3" value={settings.hero_line3} onChange={set("hero_line3")} testid="setting-hero-line3" />
          <Field id="hero-subtitle" label="Hero Subtitle" value={settings.hero_subtitle} onChange={set("hero_subtitle")} testid="setting-hero-subtitle" />
          <Field id="marquee-text" label="Marquee Text (scrolling strip)" value={settings.marquee_text} onChange={set("marquee_text")} testid="setting-marquee-text" />
        </section>

        <section className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 space-y-5">
          <h2 className="font-['Outfit'] font-bold text-lg">Razorpay</h2>
          <p className="text-zinc-500 text-xs">Get keys from dashboard.razorpay.com → Settings → API Keys. Leave empty to keep payments in demo mode.</p>
          <Field id="rzp-key-id" label="Key ID" value={settings.razorpay_key_id} onChange={set("razorpay_key_id")} placeholder="rzp_test_..." testid="setting-razorpay-key-id" />
          <Field id="rzp-secret" label="Key Secret" type="password" value={settings.razorpay_key_secret} onChange={set("razorpay_key_secret")} testid="setting-razorpay-secret" />
        </section>

        <section className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 space-y-5">
          <h2 className="font-['Outfit'] font-bold text-lg">Meta Pixel</h2>
          <p className="text-zinc-500 text-xs">Paste your Pixel ID from Meta Events Manager. PageView, Lead and Purchase events will be tracked.</p>
          <Field id="meta-pixel" label="Meta Pixel ID" value={settings.meta_pixel_id} onChange={set("meta_pixel_id")} placeholder="1234567890123456" testid="setting-meta-pixel" />
        </section>

        <button
          type="submit" disabled={saving} data-testid="settings-save-button"
          className="rounded-full bg-[#A78BFA] text-black font-bold px-8 py-3 hover:bg-[#fbbf24] transition-colors active:scale-95 disabled:opacity-60 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Settings
        </button>
      </form>

      <section className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6" data-testid="admin-users-section">
        <h2 className="font-['Outfit'] font-bold text-lg mb-2 flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#A78BFA]" /> Admin Users</h2>
        <p className="text-zinc-500 text-xs mb-5">These users can also log in to the admin panel (via password or OTP).</p>
        <ul className="space-y-2 mb-6" data-testid="admin-users-list">
          {users.map((u) => (
            <li key={u.email} className="flex items-center justify-between bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm">
              <span className="text-white">{u.email}</span>
              {u.email !== "floxora.work@gmail.com" && (
                <button
                  onClick={() => deleteUser(u.email)}
                  className="text-zinc-500 hover:text-red-400 transition-colors"
                  data-testid={`delete-user-${u.email}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
        <form onSubmit={addUser} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3" data-testid="add-user-form">
          <Input
            type="email" placeholder="user@email.com" value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            data-testid="new-user-email-input"
            className="bg-black/40 border-white/15 h-11 rounded-xl focus-visible:ring-[#A78BFA]" required
          />
          <Input
            type="password" placeholder="Password (min 8 chars)" minLength={8} value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            data-testid="new-user-password-input"
            className="bg-black/40 border-white/15 h-11 rounded-xl focus-visible:ring-[#A78BFA]" required
          />
          <button
            type="submit" disabled={userSaving} data-testid="add-user-button"
            className="rounded-full bg-[#A78BFA] text-black font-bold px-6 py-2.5 hover:opacity-90 transition-opacity active:scale-95 disabled:opacity-60 flex items-center gap-2 text-sm"
          >
            {userSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add
          </button>
        </form>
      </section>

      <section className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
        <h2 className="font-['Outfit'] font-bold text-lg mb-5 flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#A78BFA]" /> Change Password</h2>
        <form onSubmit={changePassword} className="space-y-5" data-testid="change-password-form">
          <div className="space-y-2">
            <Label htmlFor="current-pw" className="text-zinc-300">Current Password</Label>
            <Input
              id="current-pw" type="password" value={pw.current_password} data-testid="current-password-input"
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
              className="bg-black/40 border-white/15 h-11 rounded-xl focus-visible:ring-[#A78BFA]" required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-pw" className="text-zinc-300">New Password (min 8 chars)</Label>
            <Input
              id="new-pw" type="password" minLength={8} value={pw.new_password} data-testid="new-admin-password-input"
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              className="bg-black/40 border-white/15 h-11 rounded-xl focus-visible:ring-[#A78BFA]" required
            />
          </div>
          <button
            type="submit" disabled={pwSaving} data-testid="change-password-button"
            className="rounded-full border border-[#A78BFA] text-[#A78BFA] font-bold px-8 py-3 hover:bg-[#A78BFA]/10 transition-colors active:scale-95 disabled:opacity-60 flex items-center gap-2"
          >
            {pwSaving && <Loader2 className="w-4 h-4 animate-spin" />} Change Password
          </button>
        </form>
      </section>
    </div>
  );
}
